import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BackendSystems } from '@app/core/interfaces/backend-systems';
import { cached, Cached, RetentionPolicy } from '@app/utils/cached';
import { loggerFor } from '@app/utils/logger';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

export const HydraHttpSwitchHeader = 'X-HYDRA-SWITCH-SYSTEM';
export const HydraHTTPCookie = 'hydra-route';

@Injectable({
  providedIn: 'root',
})
export class BackendSystemService {
  private currentBackendSystem$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  private logger = loggerFor('BackendSystemService');

  private targetSystemOnApplicationLoad: string;

  private _locationChangeInProgressP: Promise<any>;

  // to be checked by the hydra system http interceptor, to wait bofore window reload
  set locationChangeInProgressP(value: Promise<any>) {
    // this.logger.trace('SET location change in progress')
    this._locationChangeInProgressP = value;
  }

  get locationChangeInProgressP() {
    // this.logger.trace('GET location change in progress')
    return this._locationChangeInProgressP;
  }

  private _backendSystems: Cached<BackendSystems> | null = null;

  get backendSystems(): Cached<BackendSystems> {
    if (this._backendSystems == null) {
      this._backendSystems = cached({
        dataProducer: () => this.fetchBackendSystems(),
        id: 'backend-system-service:backend-systems',
        retention: RetentionPolicy.Forever,
      });
    }

    return this._backendSystems;
  }

  private _currentBackendSystem: Cached<string> | null = null;

  get currentBackendSystem(): Cached<string> {
    if (this._currentBackendSystem == null) {
      this._currentBackendSystem = cached({
        dataProducer: () => this.fetchCurrentBackendSystem(),
        id: 'backend-system-service:current-backend-system',
        retention: RetentionPolicy.Forever,
      });
    }

    return this._currentBackendSystem;
  }

  public getHydraTargetSystemFromCookie(): string {
    const hydraRoute = this.cookieService.get(HydraHTTPCookie);
    const systemName = hydraRoute.split(':')[0];
    // console.log(`hydra-route=${hydraRoute}, system=${systemName}`);
    return systemName;
  }

  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
    private router: Router
  ) {
    this.targetSystemOnApplicationLoad = this.getHydraTargetSystemFromCookie();
    this.logger.debug('target system on application load', this.targetSystemOnApplicationLoad);
    this.currentBackendSystem$.next(this.targetSystemOnApplicationLoad);
  }

  fetchBackendSystems(): Observable<BackendSystems> {
    const url = '/middleware/api/reseller/system';
    return this.http.get<BackendSystems>(url);
  }

  fetchCurrentBackendSystem(): Observable<string> {
    return this.currentBackendSystem$.asObservable();
  }

  switchBackendSystem(payload: NewBackendSystemOptions): Observable<void> {
    let newLocation$: Observable<string>;

    // if location is not set, then after switching to the new target system
    // the path location should change to the initial location for the authenticated user-level
    if (payload.newLocation != null) {
      newLocation$ = payload.newLocation;
    } else {
      newLocation$ = of('/');
    }

    let options = {
      headers: {
        [HydraHttpSwitchHeader]: payload.newBackendSystem,
      },
    };

    return newLocation$.pipe(
      mergeMap((newLocation) => {
        this.locationChangeInProgressP = this.router.navigateByUrl(newLocation);

        // issue the request that will change the cookie value of the target system
        return this.http.head<void>('/middleware/api/reseller/system', options);
      })
    );
  }
}

interface NewBackendSystemOptions {
  newBackendSystem: string;
  newLocation?: Observable<string>;
}
